import {Nullable} from '../../../app/TypeHelpers'
import * as React from 'react'
import {ReactElement} from 'react'
import {createErrorHandlingForm, ErrorHandlingFormProps} from './hoc/createErrorHandlingForm'
import {EnterKeyHandlerProps, withEnterKeyHandler} from './hoc/withEnterKeyHandler'
import equal from 'fast-deep-equal'
import PPDateFnsUtilsForYMJP from './PPDateFnsUtilsForYMJP'
import PPDateFnsUtilsForYMD from './PPDateFnsUtilsForYMD'
import {createTheme, Input, InputAdornment, StyledEngineProvider, TextField, ThemeProvider} from '@mui/material'
import {DesktopDatePicker, LocalizationProvider, PickersDay, PickersDayProps} from '@mui/x-date-pickers'
import jaLocale from 'date-fns/locale/ja'
import {formatForDateInput, formatForDateInputFromDate} from '../../../formatters/formatDate'
import {CalendarPickerView} from '@mui/x-date-pickers/internals/models'
import withStyles from '@mui/styles/withStyles'
import {isMobile} from 'react-device-detect'
import {CalendarToday} from '@mui/icons-material'

interface State {
    value: Nullable<Date>
}

interface Props {
    onChangeDate: (date: Nullable<Date>) => void
    onChange?: (event: any, checked?: boolean) => void
    value: Nullable<Date>
    maxDate?: Nullable<Date>
    minDate?: Nullable<Date>
    autoFocus?: boolean
    holidays?: Nullable<string[]>
    disabled?: boolean
    inputType?: InputType
}

type InputType = 'ymd' | 'ym_jp'

class PPDatePicker
    extends React.Component<Props & ErrorHandlingFormProps & EnterKeyHandlerProps, State> {

    static displayName: string = 'PPDatePicker'

    constructor(props: Props & ErrorHandlingFormProps & EnterKeyHandlerProps) {
        super(props)
        this.state = {
            value: null,
        }
        this.handleDateChange = this.handleDateChange.bind(this)
    }

    componentDidMount(): void {
        if (this.props.value && this.props.value.toString() === 'Invalid Date') {
            this.setState({value: null})
        } else {
            this.setState({value: this.props.value})
        }
    }

    componentDidUpdate(
        prevProps: Readonly<Props & ErrorHandlingFormProps & EnterKeyHandlerProps>, prevState: Readonly<State>,
    ): void {
        if (!equal(prevProps, this.props)) {
            if (this.props.value && this.props.value.toString() === 'Invalid Date') {
                this.setState({value: null})
            } else {
                this.setState({value: this.props.value})
            }
        }
    }

    isInSpecifiedPeriod(date: Nullable<Date>): boolean {
        if (!date) {
            return true
        }
        const dateOnlyFormat = formatForDateInput(date.toString())
        if (this.props.minDate && dateOnlyFormat < formatForDateInput(this.props.minDate.toString())) {
            return false
        }
        if (this.props.maxDate && dateOnlyFormat > formatForDateInput(this.props.maxDate.toString())) {
            return false
        }
        return true
    }

    handleDateChange(date: Date | null, value?: string | null): void {
        let valueLength: number = 10
        //  元のmui4は未入力エリアはアンダーバー、mui5は入力エリアの内容が渡ってきている（アンダーバーなし）
        //  のため入力途中の判断は桁数で行うようにした
        if (this.props.inputType === 'ym_jp') {
            valueLength = 8
        }
        let needValueInitialize = false
        if (value && value.length !== valueLength) {
            date = null
        }
        if (date && date.toString() === 'Invalid Date') {
            date = null
            // 元のmui4は未入力エリアはアンダーバー、mui5は入力エリアの内容が渡ってきている（アンダーバーなし）
            // if (value && value.indexOf('_') === -1) {
            if (value && value.length === valueLength) {
                needValueInitialize = true
            }
        }
        if (!this.isInSpecifiedPeriod(date)) {
            date = null
            needValueInitialize = true
        }
        this.setState({
            value: needValueInitialize ? new Date('') : date,
        }, () => {
            this.props.onChangeDate(date)
            if (this.props.onChange) {
                this.props.onChange(null)
            }
        })
    }

    handleDateChangeM = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const dateString: Nullable<string> = event.target.value
        let date: Nullable<Date> = null

        if (dateString) {
            date = new Date(dateString)
            if (date.toString() === 'Invalid Date') {
                date = null
            }
        }

        // // 選択許容(min-max)範囲チェック
        // if (!this.isInSpecifiedPeriod(date)) {
        //     date = null
        // }

        this.setState({
            value: date,
        }, () => {
            this.props.onChangeDate(date)
            if (this.props.onChange) {
                this.props.onChange(null)
            }
            setTimeout(() => {
                event.target.defaultValue = ''
            })
        })
    }

    handleDateFocusM = (event: React.FocusEvent<HTMLInputElement>): void => {
        setTimeout(() => {
            event.target.defaultValue = ''
        })
    }

    formatDateOrDefault<T>(date: Date | null | undefined, defaultValue: T): string | T {
        if (!date) {
            return defaultValue
        }
        return formatForDateInputFromDate(date)
    }

    render(): ReactElement {
        const {hasError, holidays} = this.props

        let format: string
        let views: CalendarPickerView[]
        let dateFnsUtils: any
        let mask: string
        if (this.props.inputType === 'ym_jp') {
            format = 'yyyy年MM月'
            mask = '____年__月'
            views = ['year', 'month']
            dateFnsUtils = PPDateFnsUtilsForYMJP
        } else {
            format = 'yyyy/MM/dd'
            mask = '____/__/__'
            views = ['year', 'month', 'day']
            dateFnsUtils = PPDateFnsUtilsForYMD
        }

        const renderWeekEndPickerDay = (
            date: Date,
            _selectedDates: (Date | null)[],
            pickersDayProps: PickersDayProps<Date>,
        ) => {
            const switchDayColor = (getday: number) => {
                const year: number = date.getFullYear()
                const month: string = ('0' + String((date.getMonth() + 1))).slice(-2)
                const day2: string = ('0' + String(date.getDate())).slice(-2)
                const targetDate: string = `${year}-${month}-${day2}`
                if (holidays && holidays.find(
                    (holiday) => holiday === targetDate)) {
                    return {color: 'red'}
                } else {
                    switch (
                        getday // Sun=0,Sat=6
                        ) {
                        case 0:
                            return {color: 'red'}
                        case 6:
                            return {color: 'blue'}
                        default:
                            return {}
                    }
                }
            }
            const newPickersDayProps: PickersDayProps<Date> = {
                ...pickersDayProps,
                sx: switchDayColor(date.getDay()),
            }
            return <PickersDay {...newPickersDayProps} />
        }
        return <>
            {isMobile ?
                <label>
                    <InputDateForMobile
                        inputProps={{
                            min: this.formatDateOrDefault(this.props.minDate, undefined),
                            max: this.formatDateOrDefault(this.props.maxDate, undefined),
                        }}
                        endAdornment={
                            <InputAdornment position="end">
                                <CalendarToday/>
                            </InputAdornment>
                        }
                        type={'date'}
                        onChange={this.handleDateChangeM}
                        onFocus={this.handleDateFocusM}
                        value={this.formatDateOrDefault(this.state.value, '')}
                        fullWidth
                    />
                </label>
                :
                <StyledEngineProvider injectFirst>
                    <ThemeProvider theme={materialTheme}>
                        <LocalizationProvider
                            dateAdapter={dateFnsUtils}
                            adapterLocale={jaLocale}
                            dateFormats={{monthAndYear: 'yyyy年 MM月'}}>
                            <DesktopDatePicker
                                disabled={this.props.disabled}
                                autoFocus={this.props.autoFocus}
                                maxDate={this.props.maxDate === null ? undefined : this.props.maxDate}
                                minDate={this.props.minDate === null ? undefined : this.props.minDate}
                                views={views}
                                onChange={this.handleDateChange}
                                value={this.state.value}
                                inputFormat={format}
                                mask={mask}
                                disableMaskedInput={false}
                                renderInput={(params) =>
                                    <TextField
                                        {...params}
                                        error={hasError === true ? true : false}
                                        inputProps={{
                                            ...params.inputProps,
                                            autoFocus: this.props.autoFocus,
                                            // error: hasError?.toString(),
                                            variant: 'standard',
                                            margin: 'dense',
                                            placeholder: '',
                                        }}
                                        sx={{
                                            '& .MuiButtonBase-root': {
                                                marginRight: '4px',
                                            },
                                        }}
                                    />}
                                renderDay={renderWeekEndPickerDay}
                            />
                        </LocalizationProvider>
                    </ThemeProvider>
                </StyledEngineProvider>
            }
        </>
    }
}

export default createErrorHandlingForm(withEnterKeyHandler(PPDatePicker))

const materialTheme = createTheme({
    components: {
        MuiPickersDay: {
            styleOverrides: {
                root: {
                    day_disabled: {
                        color: `rgba(0, 0, 0, 0.38)!important`,
                    }
                    ,
                    day_selected: {
                        color: 'white!important',
                    },
                },
            },
        },
        MuiTextField: {
            defaultProps: {
                variant: 'standard',
                size:
                    'small',
                fullWidth: true,
            },
            styleOverrides: {
                root: {
                    //         '& .MuiInputLabel-outlined.Mui-focused': {
                    //             border: 'none',
                    //             borderBottom: '1px solid grey',
                    //         },
                },
            },
        },
        MuiFormControl: {
            styleOverrides: {
                marginDense: {
                    width: '100%',
                },
            },
        },
    },
})

const InputDateForMobile = withStyles({
    root: {
        width: '180px',
    },
})(Input)
